import { Text } from '@thanx/uikit/text'
import { PermissionResource } from 'api/roles'
import { buildTranslate } from 'locales'
import { useRef, useState } from 'react'
import { generateId } from 'utilities/uniqueId'

import { PermissionRadio } from './PermissionRadio'

export type PermissionValue = 'view_and_edit' | 'can_view' | 'no_access'

type Props = {
  resource: PermissionResource
  value: PermissionValue
  onChange: (name: string, value: PermissionValue) => void
  disabled: boolean
  isLast?: boolean
  lockedOptions?: Set<PermissionValue>
  lockedTooltip?: string
}

const t = buildTranslate('dashboard_access.edit_role.permission_levels')
const tDesc = buildTranslate(
  'dashboard_access.edit_role.permission_descriptions'
)
const tLocationNote = buildTranslate(
  'dashboard_access.edit_role.location_restriction_notes'
)

const DESCRIPTION_KEYS = new Set([
  'campaigns',
  'campaign_collaboration',
  'campaign_approvals',
  'segments',
  'segment_collaboration',
  'smart_links',
  'feedback',
  'members',
  'merchant_users',
  'billing',
  'merchant_settings',
  'loyalty',
  'reward_templates',
  'tiers_program',
  'points_multipliers',
  'reward_issuance',
  'reward_redemptions',
  'games',
  'locations',
  'ordering_configuration',
  'content_management',
  'app_configuration',
  'digital_experiences',
  'reporting',
  'reporting_location',
])

const getDescription = (resourceName: string): string | undefined => {
  if (!DESCRIPTION_KEYS.has(resourceName)) return undefined
  return tDesc(resourceName)
}

const LOCATION_NOTE_KEYS = new Set([
  'campaigns',
  'targeted_campaigns',
  'automated_campaigns',
  'segments',
  'feedback',
  'members',
  'locations',
  'ordering_configuration',
  'reporting_location',
])

const getLocationNote = (resourceName: string): string | undefined => {
  if (!LOCATION_NOTE_KEYS.has(resourceName)) return undefined
  return tLocationNote(resourceName)
}

export const PERMISSION_OPTIONS: { label: string; val: PermissionValue }[] = [
  { label: t('view_and_edit'), val: 'view_and_edit' },
  { label: t('can_view'), val: 'can_view' },
  { label: t('no_access'), val: 'no_access' },
]

export const PERMISSION_OPTION_GAP = '24px'

export const PermissionRow = ({
  resource,
  value,
  onChange,
  disabled,
  isLast = false,
  lockedOptions,
  lockedTooltip,
}: Props) => {
  const description = getDescription(resource.name)
  const locationNote = getLocationNote(resource.name)
  const [isLocationNoteOpen, setIsLocationNoteOpen] = useState(false)
  const tooltipId = useRef(
    `locked-tooltip-${resource.name}-${generateId()}`
  ).current

  const rowClassName = ['py-s', !isLast && 'border-bottom-1 grey-20-border']
    .filter(Boolean)
    .join(' ')

  return (
    <div className={rowClassName}>
      <div className="d-flex justify-content-between align-items-center">
        <div className="mr-m" style={{ flex: '1 1 0', minWidth: 0 }}>
          <Text.BodyText3 className="m-0 d-block">
            {resource.display_name}
          </Text.BodyText3>
          {description && (
            <div className="mt-xs">
              {description
                .split(/\.\s+/)
                .filter(Boolean)
                .map((sentence, i) => (
                  <Text.BodyText4
                    key={i}
                    color="grey70"
                    className="m-0 d-block"
                  >
                    {sentence.endsWith('.') ? sentence : `${sentence}.`}
                  </Text.BodyText4>
                ))}
            </div>
          )}
          {locationNote && (
            <div className="mt-xs">
              <button
                type="button"
                className="p-0 border-0 bg-transparent font-size-12 belize-hole-50 text-left"
                style={{ cursor: 'pointer' }}
                onClick={() => setIsLocationNoteOpen(!isLocationNoteOpen)}
                aria-expanded={isLocationNoteOpen}
              >
                {tLocationNote('toggle_label')}
                <i
                  className={`fa ${
                    isLocationNoteOpen ? 'fa-caret-down' : 'fa-caret-right'
                  } ml-xs`}
                  aria-hidden="true"
                />
              </button>
            </div>
          )}
        </div>
        <div
          className="d-flex align-items-center"
          style={{ gap: PERMISSION_OPTION_GAP, flexShrink: 0 }}
        >
          {PERMISSION_OPTIONS.map(option => (
            <PermissionRadio
              key={option.val}
              option={option}
              resourceName={resource.name}
              checked={value === option.val}
              disabled={disabled}
              isLocked={lockedOptions?.has(option.val) ?? false}
              lockedTooltip={lockedTooltip}
              tooltipId={tooltipId}
              onChange={onChange}
            />
          ))}
        </div>
      </div>
      {locationNote && isLocationNoteOpen && (
        <Text.BodyText4 color="grey70" className="m-0 mt-xxs d-block">
          {locationNote}
        </Text.BodyText4>
      )}
    </div>
  )
}
